import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Store, PlusCircle, Building2, Pencil, Eye, Power, PowerOff } from 'lucide-react'
import {
  activateEmpresaSociedad,
  createEmpresaSociedad,
  deactivateEmpresaSociedad,
  fetchEmpresa,
  updateEmpresaSociedad,
  type EmpresaSociedadBrief,
  type SociedadCreatePayload,
} from '../../api'
import { useAuth } from '../../AuthContext'
import { PageHeader } from '../../components/PageHeader'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Badge } from '../../components/ui/badge'
import { Skeleton } from '../../components/ui/skeleton'
import { Card } from '../../components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'

const emptyForm: SociedadCreatePayload = {
  nombre: '',
  nombre_comercial: '',
  tipo_identificacion: '',
  numero_identificacion: '',
  correo_electronico: '',
  telefono: '',
  direccion: '',
  codigo_actividad: '',
}

function cleanPayload(form: SociedadCreatePayload): SociedadCreatePayload {
  // Los campos opcionales vacíos viajan como null en vez de "" — el backend
  // los valida como opcionales, no como strings vacíos.
  const cleaned = { ...form } as Record<string, string | null>
  for (const key of Object.keys(cleaned)) {
    if (key !== 'nombre' && cleaned[key] === '') cleaned[key] = null
  }
  return cleaned as unknown as SociedadCreatePayload
}

function toForm(s: EmpresaSociedadBrief): SociedadCreatePayload {
  return {
    nombre: s.nombre,
    nombre_comercial: s.nombre_comercial ?? '',
    tipo_identificacion: s.tipo_identificacion ?? '',
    numero_identificacion: s.numero_identificacion ?? '',
    correo_electronico: s.correo_electronico ?? '',
    telefono: s.telefono ?? '',
    direccion: s.direccion ?? '',
    codigo_actividad: s.codigo_actividad ?? '',
  }
}

function SociedadFormFields({
  form,
  onChange,
  idPrefix,
}: {
  form: SociedadCreatePayload
  onChange: (form: SociedadCreatePayload) => void
  idPrefix: string
}) {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-nombre`}>Nombre / Razón social *</Label>
        <Input
          id={`${idPrefix}-nombre`}
          required
          value={form.nombre}
          onChange={(e) => onChange({ ...form, nombre: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-nombre-comercial`}>Nombre comercial</Label>
        <Input
          id={`${idPrefix}-nombre-comercial`}
          value={form.nombre_comercial ?? ''}
          onChange={(e) => onChange({ ...form, nombre_comercial: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-tipo-id`}>Tipo de identificación</Label>
          <Input
            id={`${idPrefix}-tipo-id`}
            placeholder="Jurídica, Física..."
            value={form.tipo_identificacion ?? ''}
            onChange={(e) => onChange({ ...form, tipo_identificacion: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-numero-id`}>Número de identificación</Label>
          <Input
            id={`${idPrefix}-numero-id`}
            value={form.numero_identificacion ?? ''}
            onChange={(e) => onChange({ ...form, numero_identificacion: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-correo`}>Correo electrónico</Label>
          <Input
            id={`${idPrefix}-correo`}
            type="email"
            value={form.correo_electronico ?? ''}
            onChange={(e) => onChange({ ...form, correo_electronico: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-telefono`}>Teléfono</Label>
          <Input
            id={`${idPrefix}-telefono`}
            value={form.telefono ?? ''}
            onChange={(e) => onChange({ ...form, telefono: e.target.value })}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-direccion`}>Dirección</Label>
        <Input
          id={`${idPrefix}-direccion`}
          value={form.direccion ?? ''}
          onChange={(e) => onChange({ ...form, direccion: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-codigo-actividad`}>Código de actividad</Label>
        <Input
          id={`${idPrefix}-codigo-actividad`}
          value={form.codigo_actividad ?? ''}
          onChange={(e) => onChange({ ...form, codigo_actividad: e.target.value })}
        />
      </div>
    </>
  )
}

export function EmpresaSociedadesPage() {
  const { user, activeSociedadId, setActiveSociedadId } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const empresaId = user?.sociedades.find((s) => s.id === activeSociedadId)?.empresa_id
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<SociedadCreatePayload>(emptyForm)
  const [editTarget, setEditTarget] = useState<EmpresaSociedadBrief | null>(null)
  const [editForm, setEditForm] = useState<SociedadCreatePayload>(emptyForm)

  const query = useQuery({
    queryKey: ['empresa', empresaId],
    queryFn: () => fetchEmpresa(empresaId!),
    enabled: !!empresaId,
  })

  function invalidate(): void {
    queryClient.invalidateQueries({ queryKey: ['empresa', empresaId] })
  }

  const createMutation = useMutation({
    mutationFn: () => createEmpresaSociedad(empresaId!, cleanPayload(form)),
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setForm(emptyForm)
      toast.success('Sociedad creada — ya podés asignarle un Administrador.')
    },
    onError: () => toast.error('No se pudo crear la Sociedad.'),
  })

  const updateMutation = useMutation({
    mutationFn: () => updateEmpresaSociedad(empresaId!, editTarget!.id, cleanPayload(editForm)),
    onSuccess: () => {
      invalidate()
      setEditTarget(null)
      toast.success('Sociedad actualizada.')
    },
    onError: () => toast.error('No se pudo actualizar la Sociedad.'),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: (s: EmpresaSociedadBrief) =>
      s.is_active ? deactivateEmpresaSociedad(empresaId!, s.id) : activateEmpresaSociedad(empresaId!, s.id),
    onSuccess: (updated) => {
      invalidate()
      toast.success(updated.is_active ? `${updated.nombre} reactivada.` : `${updated.nombre} desactivada — el bot deja de responder ahí.`)
    },
    onError: () => toast.error('No se pudo cambiar el estado de la Sociedad.'),
  })

  function verSociedad(s: EmpresaSociedadBrief): void {
    setActiveSociedadId(s.id)
    navigate('/app/conversaciones')
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    createMutation.mutate()
  }

  function handleEditSubmit(e: FormEvent): void {
    e.preventDefault()
    updateMutation.mutate()
  }

  function openEdit(s: EmpresaSociedadBrief): void {
    setEditTarget(s)
    setEditForm(toForm(s))
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Store}
        title="Sociedades"
        description={
          query.data
            ? `${query.data.sociedades.length} ${query.data.sociedades.length === 1 ? 'Sociedad' : 'Sociedades'} en ${query.data.nombre}.`
            : 'Los negocios/marcas de tu Empresa — cada una con su propio WhatsApp, contactos y conversaciones.'
        }
        action={
          <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) setForm(emptyForm) }}>
            <Button onClick={() => setOpen(true)}>
              <PlusCircle className="size-4" />
              Nueva Sociedad
            </Button>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <DialogHeader>
                  <DialogTitle>Nueva Sociedad</DialogTitle>
                </DialogHeader>
                <SociedadFormFields form={form} onChange={setForm} idPrefix="sociedad" />
                <DialogFooter>
                  <Button type="submit" disabled={createMutation.isPending}>
                    Crear
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="overflow-hidden py-0">
        {query.isLoading && (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
          </div>
        )}
        {query.isError && <p className="p-5 text-sm text-destructive">No se pudieron cargar las Sociedades.</p>}
        {query.data && query.data.sociedades.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
            <Building2 className="size-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Todavía no creaste ninguna Sociedad.</p>
          </div>
        )}
        {query.data && query.data.sociedades.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Identificación</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Alta</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.sociedades.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted">
                        <Store className="size-4 text-muted-foreground" />
                      </div>
                      <div className="flex flex-col">
                        {s.nombre}
                        {s.nombre_comercial && (
                          <span className="text-xs font-normal text-muted-foreground">{s.nombre_comercial}</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {s.numero_identificacion
                      ? `${s.tipo_identificacion ? s.tipo_identificacion + ' — ' : ''}${s.numero_identificacion}`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{s.correo_electronico ?? s.telefono ?? '—'}</TableCell>
                  <TableCell>
                    <Badge variant={s.is_active ? 'outline' : 'destructive'}>
                      {s.is_active ? 'Activa' : 'Desactivada'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(s.created_at).toLocaleDateString('es-CR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => verSociedad(s)}>
                        <Eye className="size-3.5" />
                        Ver
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>
                        <Pencil className="size-3.5" />
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={toggleActiveMutation.isPending}
                        onClick={() => toggleActiveMutation.mutate(s)}
                      >
                        {s.is_active ? (
                          <>
                            <PowerOff className="size-3.5 text-destructive" />
                            Desactivar
                          </>
                        ) : (
                          <>
                            <Power className="size-3.5" />
                            Activar
                          </>
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(next) => !next && setEditTarget(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <form onSubmit={handleEditSubmit} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>Editar {editTarget?.nombre}</DialogTitle>
            </DialogHeader>
            <SociedadFormFields form={editForm} onChange={setEditForm} idPrefix="sociedad-edit" />
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setEditTarget(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                Guardar cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
